/**
 * Batch Image Upload Component with Drag & Drop
 * Supports multiple image uploads with preview and delete
 */

import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import toast from 'react-hot-toast';
import cmsApiService from '../../services/cmsApiService';

interface BatchImageUploadProps {
  images: Array<{ imageUrl: string; id?: number }>;
  onChange: (images: Array<{ imageUrl: string; id?: number }>) => void;
  onUploadComplete?: (urls: string[]) => void;
  onDelete?: (imageId: number) => Promise<boolean>; // Handler for deleting images from backend
  folder?: string;
  maxSize?: number; // in MB per image
  maxFiles?: number; // max number of images
  className?: string;
  label?: string;
  description?: string;
}

export const BatchImageUpload: React.FC<BatchImageUploadProps> = ({
  images = [],
  onChange,
  onUploadComplete,
  onDelete,
  folder = 'gallery',
  maxSize = 5,
  maxFiles = 20,
  className = '',
  label = 'Upload Images',
  description = 'Drag and drop images here or click to browse',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: `${file.name} is not an image` };
    }

    // Check file size
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      return { valid: false, error: `${file.name} exceeds ${maxSize}MB` };
    }

    return { valid: true };
  };

  const uploadFiles = async (files: FileList) => {
    const fileArray = Array.from(files);
    
    // Check max files
    if (images.length + fileArray.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      return;
    }

    // Validate all files first
    const validFiles: File[] = [];
    for (const file of fileArray) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        toast.error(validation.error || 'Invalid file');
      }
    }

    if (validFiles.length === 0) {
      return;
    }

    try {
      setIsUploading(true);
      setUploadingCount(validFiles.length);
      setUploadedCount(0);

      const uploadedUrls: string[] = [];

      // Upload files one by one
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        
        try {
          const response = await cmsApiService.uploadImage(file, folder);
          const imageUrl = response.url || response.imageUrl || response;
          uploadedUrls.push(imageUrl);
          setUploadedCount(i + 1);
        } catch (error) {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      if (uploadedUrls.length > 0) {
        // Add new images to existing ones
        const newImages = uploadedUrls.map(url => ({ imageUrl: url }));
        const updatedImages = [...images, ...newImages];
        onChange(updatedImages);
        
        if (onUploadComplete) {
          onUploadComplete(uploadedUrls);
        }
        
        toast.success(`Successfully uploaded ${uploadedUrls.length} image(s)`);
      }
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setIsUploading(false);
      setUploadingCount(0);
      setUploadedCount(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      uploadFiles(files);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        uploadFiles(files);
      }
    },
    [images.length]
  );

  const handleRemoveImage = async (index: number) => {
    const image = images[index];
    
    // If image has an ID, it exists in the database and needs to be deleted via API
    if (image.id && onDelete) {
      try {
        const success = await onDelete(image.id);
        if (!success) {
          return;
        }
        toast.success('Image deleted successfully');
      } catch (error) {
        toast.error('Failed to delete image');
        return;
      }
    }
    
    // Remove from local state (for new images or after successful backend delete)
    const updatedImages = images.filter((_, i) => i !== index);
    onChange(updatedImages);
  };

  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <Card
        className={`border-2 border-dashed transition-colors cursor-pointer ${
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-primary/50'
        } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="p-8">
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            {isUploading ? (
              <>
                <Loader className="w-12 h-12 animate-spin text-primary" />
                <div>
                  <p className="font-medium">Uploading images...</p>
                  <p className="text-sm text-muted-foreground">
                    {uploadedCount} of {uploadingCount} uploaded
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-lg">{label}</p>
                  <p className="text-sm text-muted-foreground mt-1">{description}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Maximum {maxSize}MB per image • {images.length}/{maxFiles} images
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Browse Images
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">
            Uploaded Images ({images.length})
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map((image, index) => (
              <div
                key={`${image.imageUrl}-${index}`}
                className="relative group aspect-square rounded-lg overflow-hidden border bg-muted"
              >
                <img
                  src={image.imageUrl}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    type="button"
                    size="sm"
                    variant="red"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleRemoveImage(index);
                    }}
                    className="shadow-lg"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
